<template>
  <div class="h-full">
    <DateInput
      v-if="!isRange"
      class="my-4"
      :date="internalState.date"
      :minDate="minDate"
      :maxDate="maxDate"
      @dateSelected="handleCustomDateSelected"
    />
    <template v-else>
      <DateRange
        v-if="isSelectingCustomDate"
        class="my-4"
        :range="internalState.range"
        @rangeSelected="handleCustomRangeSelected"
      />
      <DateShortcutsList
        v-else
        class="mb-4"
        :activeShortcut="internalState.shortcutId"
        @shortcutSelected="handleShortcutSelected"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { DateShortcut, DateShortcutsList } from "./DateShortcutsList";
import { DateInput, DateRange } from "@/Components/Date";
import { isEqual } from "lodash";
const emit = defineEmits(["apply", "isLoaded"]);

const props = defineProps({
  /**
   * {
   *   date: Date,
   *   range: {
   *     start: Date,
   *     end: Date,
   *   },
   *   shortcutId: DateShortcut
   * }
   */
  initialState: {
    type: Object,
    default: () => ({}),
  },
  isRange: { type: Boolean, default: false },
  minDate: { type: Date, default: null },
  maxDate: { type: Date, default: null },
});

const internalState = ref({ ...props.initialState });
const isSelectingCustomDate = ref(
  internalState.value.shortcutId === DateShortcut.CUSTOM
);

const disableApply = computed(() => {
  if (!props.isRange) {
    // Create a date: newDate
    let newDate = internalState.value.date
      ? new Date(internalState.value.date).setHours(23, 59, 59, 0)
      : null;

    // Create a date: oldDate
    let oldDate = props.initialState.date
      ? new Date(props.initialState.date).setHours(23, 59, 59, 0)
      : null;

    // Compare the dates and disable when the values are equal.
    return isEqual(newDate, oldDate);
  }

  return !internalState.value.range?.start || !internalState.value.range?.end;
});

onMounted(() => {
  emit("isLoaded");
});

function handleShortcutSelected({ shortcutId, shortcutRange }) {
  if (shortcutId === DateShortcut.CUSTOM) {
    isSelectingCustomDate.value = true;
    return;
  }

  internalState.value.range = shortcutRange;
  internalState.value.shortcutId = shortcutId;
}

function handleCustomDateSelected(date) {
  internalState.value.date = date;
  internalState.value.shortcutId = DateShortcut.CUSTOM;
}

function handleCustomRangeSelected(range) {
  internalState.value.range = range;
  internalState.value.shortcutId = DateShortcut.CUSTOM;
}

function handleBack() {
  isSelectingCustomDate.value = false;
}

function handleApply() {
  emit("apply", internalState.value);
}

defineExpose({
  showBack: isSelectingCustomDate,
  showApply: true,
  showSearch: false,
  disableApply,
  handleBack,
  handleApply,
});
</script>
